<template>
  <div
    class="product-options"
    :class="{ [$class('loading')]: loading }"
  >
    <div
      v-for="(option) of options"
      :key="option.key"
      class="product-options__option"
      :class="{ [$class('hidden')]: !option.settings.displayOption }"
    >
      <legend
        v-if="product.giftCard"
        class="product-options__option-title text-body-s-regular"
        v-text="
          !variant ?
            $string('product.form.gift_card_select_label') :
            $string('product.form.gift_card_selected_label')
        "
      />
      <legend
        v-else-if="displayOptionName && !(option.name.toLowerCase() === 'text')"
        class="product-options__option-title text-body-m-bold"
        v-text=" $string('product.settings.select') + ' ' + option.name"
      />

      <div
        v-if="option.settings.type === 'dropdown' || product.giftCard"
        class="product-options__select-group form-group"
      >
        <label
          v-if="!(option.name.toLowerCase() === 'text')"
          class="visually-hidden"
          :for="option.key"
          v-text="option.name"
        />

        <span
          v-if="loading"
          class="product-options__select-placeholder"
          v-text="$string('accessibility.loading')"
        />

        <select
          v-if="!(option.name.toLowerCase() === 'text')"
          :id="option.key"
          v-model="currentOptions[option.index]"
          class="product-options__select"
        >
          <option
            v-if="option.settings.excludeFromInitialSelect && product.giftCard"
            value=""
            v-text="$string('product.form.select_amount')"
          />

          <option
            v-for="(value) of option.validValues"
            :id="value.key"
            :key="value.key"
            :disabled="value.disabled"
            :value="value.value"
            v-text="getDropdownLabel(value)"
          />
        </select>
      </div>

      <div
        v-else
        class="product-options__values"
      >
        <selection-tab
          v-for="(value) of option.validValues"
          :id="value.key"
          :key="value.key"
          v-model="currentOptions[option.index]"
          class="product-options__value"
          :disabled="value.disabled"
          :label="value.label"
          :loading="loading"
          :name="option.key"
          :value="value.value"
          :visually-disabled="value.visuallyDisabled"
        />
      </div>
    </div>

    <select
      v-model="currentVariantId"
      class="product-options__variant-select visually-hidden"
      name="id"
      :aria-label="$string('product.form.variant_selector')"
    >
      <option
        v-for="(productVariant) of product.variants"
        :key="productVariant.id"
        :value="productVariant.id"
        v-text="`${productVariant.title} - ${productVariant.id}`"
      />
    </select>
  </div>
</template>

<script>

/**
 * Vue: Product options (product-form)
 * -----------------------------------------------------------------------------
 * Product variants split into separate options and values.
 *
 * @param {Boolean} [loading] - Loading state.
 * @param {Boolean|Object} product - Canvas formatted product object.
 * @param {String} sectionId - Section ID.
 * @param {Object} settings - Product form settings.
 * @param {Boolean|Null|Object} variant - Canvas formatted variant object.
 *
 * @emits updateVariant - Emit new variant object.
 *
 */
import SelectionTab from '~async/utils/selection-tab/selection-tab'

import { convertToHandle } from '~/helpers/convert'
import { emitUpdateVariant } from '~/helpers/vue'

export default {
  name: 'ProductOptions',

  components: {
    SelectionTab,
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    sectionId: {
      type: String,
      default: '',
      required: true,
    },
    settings: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    variant: {
      type: [Boolean, null, Object],
      default: false,
      required: true,
    },
  },

  emits: {
    'update-variant': emitUpdateVariant,
  },

  data() {
    return {
      currentVariantId: 0,
      currentOptions: [],
    }
  },

  computed: {

    /**
     * Compute if option name should be displayed.
     * @returns {Boolean}
     */
    displayOptionName() {
      if (this.product.giftCard && this.settings.hideGiftCardOptionNames) {
        return false
      }

      if (
        this.settings.displaySingleOptionNames ||
        (this.settings.displaySingleValueOptions && this.options.length > 1)
      ) {
        return true
      }

      const multiValueOptions = this.options.filter((option) => {
        return option.values.length > 1
      })

      return multiValueOptions.length > 1
    },

    /**
     * @typedef {Object} ProductOption - Formatted Product Options
     * @property {String} handle - Option handle.
     * @property {Number} index - Option forloop index.
     * @property {String} key - Unique option key/ID.
     * @property {Array} labels - Label strings.
     * @property {Boolean} liquid - Rendered from Liquid data.
     * @property {String} name - Option name.
     * @property {ProductOptionSettings} settings - Display settings.
     * @property {Array<ProductOptionValue>} validValues - Array of values
     * filtered by if they're valid, only when hideInvalidValues is true.
     * @property {Array<ProductOptionValue>} values - Array of values.
     */

    /**
     * Compute options.
     * - Builds options array with extended data.
     * - When `loading` is `true` and `product` is `false` then this method
     *   returns placeholder options to render in the loading state.
     * @returns {Array<ProductOption>}
     */
    options() {
      if (this.loading && !this.product) {
        return this.getOptionsPlaceholder()
      }

      if (!this.product?.options?.length) {
        return []
      }

      /**
       * Build extended options and values data.
       */
      return this.product.options.map((option) => {
        const handle = convertToHandle(option.name)
        const key = `${this.sectionId}-${this.product.handle}-option-${option.index}-${handle}`
        const values = this.getOptionValues(option, key)
        const settings = this.getOptionSettings(handle, values)

        const validValues = this.settings.hideInvalidValues
          ? values.filter((value) => value.valid)
          : values

        return {
          ...option,
          handle,
          key,
          settings,
          validValues,
          values,
        }
      })
    },
  },

  watch: {

    /**
     * Watch current options to update current variant and emit.
     * - Only update variant when all options have values.
     * @param {Boolean|Object} newValue - New current options.
     * @param {Boolean|Object} oldValue - Old current options.
     */
    currentOptions: {
      handler(newValue, oldValue) {
        if (
          !this.currentOptions.length ||
          this.currentOptions.length !== this.options?.length
        ) {
          return
        }

        /**
         * If current options previously matched options length but now doesn't
         * then emit null.
         * - Assumes empty dropdown option value has been selected.
         */
        if (
          oldValue.length === this.options?.length &&
          newValue.filter(Boolean)?.length !== this.options?.length
        ) {
          this.currentVariantId = 0
          this.$emit('update-variant', null)
          return
        }

        /**
         * Find variant based on current options and emit.
         */
        const variantTitle = this.currentOptions.join(' / ')
        const variant = this.getVariantFromProperty(variantTitle, 'title')

        if (variant?.id === this.variant?.id) {
          return
        }

        this.currentVariantId = variant?.id || 0
        this.$emit('update-variant', variant)
      },
      deep: true,
    },

    /**
     * Watch product change to set variant from URL.
     * @param {Boolean|Object} newValue - New product value.
     * @param {Boolean|Object} oldValue - Old product value.
     */
    product: {
      handler(newValue, oldValue) {
        if (newValue?.id === oldValue?.id) {
          return
        }

        this.setInitialState()
      },
      immediate: true,
    },

    /**
     * Watch variant change to reset product options when false.
     */
    variant() {
      if (this.variant || this.variant === null) {
        return
      }

      this.setInitialState()
    },
  },

  methods: {

    /**
     * Get option value dropdown label.
     * @param {ProductOptionValue} value - Option value.
     * @returns {String}
     */
    getDropdownLabel(value) {
      if (value.available) {
        return value.label
      }

      const state = value.valid
        ? this.$string('product.form.sold_out')
        : this.$string('product.form.invalid')

      return `${value.label} - ${state}`
    },

    /**
     * Get options placeholder data.
     * @returns {Array<ProductOption>}
     */
    getOptionsPlaceholder() {
      return [...new Array(3)].map((_, index) => {
        const name = `${this.$string('accessibility.loading')} ${index}`

        const option = {
          index,
          labels: [...new Array(3)].map((__, labelIndex) => `${name} ${labelIndex}`),
          name,
          values: [...new Array(3)].map((__, valueIndex) => `${name} ${valueIndex}`),
        }

        const handle = convertToHandle(option.name)
        const key = `${handle}-key`
        const values = this.getOptionValues(option, key)
        const settings = this.getOptionSettings(handle, values)

        return {
          ...option,
          handle,
          key,
          settings,
          validValues: values,
          values,
        }
      })
    },

    /**
     * @typedef {Object} ProductOptionValue
     * @property {Boolean} available - If the value is available.
     * @property {Boolean} disabled - If the value input should be disabled,
     * only true if value is unavailable and selectDisabledValues is false.
     * @property {String} handle - Value handle.
     * @property {Number} index - Value forloop index.
     * @property {String} key - Unique value key/ID.
     * @property {String} label - Value label.
     * @property {Array} matchingVariants - Variants that feature value.
     * @property {Boolean} valid - If the value combination exists as a variant.
     * @property {String} value - Escaped input value.
     * @property {Boolean} visuallyDisabled - If the value input should appear
     * to be disabled.
     */

    /**
     * Get values objects.
     * @param {Object} option - Option object.
     * @param {String} optionKey - Unique option key.
     * @returns {Array<ProductOptionValue>}
     */
    getOptionValues(option, optionKey) {
      return option.values.map((value, index) => {
        const handle = convertToHandle(value)
        const key = `${optionKey}-value-${index}-${handle}`
        const label = option.labels[index]

        /**
         * Find variants which match option values.
         * - First find variants that match forloop value.
         * - Then find variants that match other currentOptions.
         */
        const matchingVariants = this.product?.variants?.length
          ? this.product.variants
            .filter((variant) => variant.selectedOptions[option.index].value === value)
            .filter((variant) => {
              return this.currentOptions.every((currentOption, currentOptionIndex) => {
                if (!currentOption || option.index === currentOptionIndex) {
                  return true
                }

                return variant.selectedOptions[currentOptionIndex]?.value === currentOption
              })
            })
          : []

        /**
         * Determine if an option value is valid.
         * - Hide an option value where the current options don't exist as a
         *   variant, otherwise it appears as unavailable.
         * - Find if current option in forloop exists in matching variants.
         * - If no length then option value isn't valid.
         */
        const valid = option.values.some((optionValue) => {
          return matchingVariants.some((variant) => {
            return variant.selectedOptions[option.index].value === optionValue
          })
        })

        /**
         * Determine available based on matched variant availability.
         * - Variant isn't available if it isn't valid.
         */
        const available = valid
          ? matchingVariants.some((variant) => variant.available)
          : false

        /**
         * Determine if value is disabled based on availability and settings.
         */
        const disabled = !available && !this.settings.selectDisabledValues
        const visuallyDisabled = !available && this.settings.selectDisabledValues

        /**
         * Return data.
         */
        return {
          available,
          disabled,
          handle,
          index,
          key,
          label,
          matchingVariants,
          valid,
          value,
          visuallyDisabled,
        }
      })
    },

    /**
     * @typedef {Object} ProductOptionSettings
     * @property {Boolean} displayOption - Display option and its values.
     * @property {Boolean} excludeFromInitialSelect - If initialSelect is true
     * whether the option should be excluded. Gift card are excluded.
     * @property {String} type - Type of option, `dropdown` or `tab`.
     */

    /**
     * Get option settings.
     * @param {String} handle - Option handle.
     * @param {Array} values - Formatted values array.
     * @returns {ProductOptionSettings}
     */
    getOptionSettings(handle, values) {
      const displayOption = this.settings.displaySingleValueOptions
        ? true
        : values.length > 1

      const excludeFromInitialSelect =
        !this.settings.initialSelect ||
        this.settings.excludedInitialSelectOptions?.includes(handle) ||
        this.product.giftCard

      const type = this.settings.dropdownOptions.includes('dropdown') || this.product.giftCard
        ? 'dropdown'
        : 'tab'

      return {
        displayOption,
        excludeFromInitialSelect,
        type,
      }
    },

    /**
     * Set initial state.
     */
    setInitialState(ignoreUrl) {
      this.currentOptions = []
      const variant = this.getVariantFromDefault(ignoreUrl)

      if (!variant) {
        // Set the first value of each option as the default selection
        this.currentOptions = this.options.map((option) => {
          return option.validValues.length > 0 ? option.validValues[0].value : ''
        })
        return
      }

      variant.selectedOptions.forEach((selectedOption, index) => {
        this.currentOptions[index] = selectedOption.value
      })
    },

    /**
     * Get variant from default state.
     * - From URL or initial select.
     * @returns {Boolean|Object}
     */
    getVariantFromDefault() {
      if (this.$variable('storybook') && this.variant) {
        return false
      }

      /**
       * Always get current variant from URL.
       */
      const variantFromUrl = this.getVariantFromUrl()

      if (variantFromUrl) {
        return variantFromUrl
      }

      /**
       * Do not set current variant if disabled in settings.
       */
      if (!this.settings.initialSelect) {
        return false
      }

      /**
       * Pick the first in stock variant.
       * - If no variants are in stock then use first variant.
       */
      const inStock = this.product.variants.filter((variant) => variant.available)
      const variant = inStock.length ? inStock[0] : this.product.variants[0]

      /**
       * Only set current variant if none of the variant options are excluded
       * from being initially set.
       * - Except where excluded option has only a single value, otherwise if
       *   single value options are not shown the variant will not be
       *   selectable by the user.
       */
      const currentOptions = variant.selectedOptions.map((selectedOption) => {
        const selectedOptionHandle = convertToHandle(selectedOption.name)

        const matchedOption = this.options.find((option) => {
          return option.handle === selectedOptionHandle
        })

        const excludedOption =
          (this.settings.excludedInitialSelectOptions?.includes(selectedOptionHandle) ||
            this.product.giftCard) &&
          matchedOption?.values?.length > 1

        return excludedOption ? '' : selectedOption.value
      })

      const filteredCurrentOptions = currentOptions.filter(Boolean)

      if (filteredCurrentOptions.length !== variant.selectedOptions.length) {
        this.currentOptions = currentOptions
        return false
      }

      return variant
    },

    /**
     * Get variant from URL parameter.
     * @returns {Object}
     */
    getVariantFromUrl() {
      const params = new URLSearchParams(document.location.search.substring(1))
      const variantId = Number(params.get('variant'))
      return this.getVariantFromProperty(variantId)
    },

    /**
     * Get variant from variant property.
     * @param {String} value - Value to match.
     * @param {String} property - Property to find by.
     * @returns {Object|Null}
     */
    getVariantFromProperty(value, property = 'id') {
      const matchingVariant = this.product.variants?.find((variant) => {
        return variant[property] === value
      })

      return matchingVariant ? matchingVariant : null
    },
  },
}
</script>

<style lang="scss">
@import './product-options';
</style>
